import { useState, useEffect, useId } from "react";
import type { ClientUserFormData } from "../types/index.js";
import { apiGet } from "../lib/api.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ClientUserFormProps {
	projectId: string;
	onSubmit: (data: ClientUserFormData) => Promise<void>;
	onCancel: () => void;
	status?: "idle" | "loading" | "success" | "error";
	errorMessage?: string;
}

interface ValidationErrors {
	companyName?: string;
	name?: string;
	email?: string;
}

// ---------------------------------------------------------------------------
// Email validation helper
// ---------------------------------------------------------------------------

function isValidEmail(value: string): boolean {
	// Standard RFC-5322 simplified pattern — sufficient for UX validation
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Modal form for adding a client user to a project.
 * Opened when the user types `/c ` in the chat input.
 *
 * Collects: Company Name (required), Name (required), Email (required),
 * Job Title (optional), Project Role (optional).
 */
export default function ClientUserForm({
	projectId,
	onSubmit,
	onCancel,
	status = "idle",
	errorMessage,
}: ClientUserFormProps) {
	const headingId = useId();

	// Form field state
	const [companyName, setCompanyName] = useState("");
	const [name, setName] = useState("");
	const [email, setEmail] = useState("");
	const [jobTitle, setJobTitle] = useState("");
	const [projectRole, setProjectRole] = useState("");

	// Validation error state — only surfaced after a submit attempt
	const [submitted, setSubmitted] = useState(false);
	const [errors, setErrors] = useState<ValidationErrors>({});

	// Company autocomplete — fetched on mount from /api/client-companies
	const [companies, setCompanies] = useState<string[]>([]);
	const datalistId = useId();

	const isLoading = status === "loading";

	// -----------------------------------------------------------------------
	// Fetch companies for autocomplete on mount
	// -----------------------------------------------------------------------

	useEffect(() => {
		let cancelled = false;

		(async () => {
			try {
				const response = await apiGet("/api/client-companies");
				if (cancelled) return;

				if (response.ok) {
					const data = (await response.json()) as { companies: Array<{ id: string; name: string } | string> };
					if (!cancelled && Array.isArray(data.companies)) {
						const names = data.companies.map((c) =>
							typeof c === "string" ? c : c.name,
						);
						setCompanies(names);
					}
				}
			} catch {
				// Network error — autocomplete simply won't populate. Not a crash.
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	// -----------------------------------------------------------------------
	// Validation
	// -----------------------------------------------------------------------

	function validate(): ValidationErrors {
		const errs: ValidationErrors = {};

		if (!companyName.trim()) {
			errs.companyName = "Company Name is required.";
		}
		if (!name.trim()) {
			errs.name = "Name is required.";
		}
		if (!email.trim()) {
			errs.email = "Email is required.";
		} else if (!isValidEmail(email)) {
			errs.email = "Please enter a valid email address.";
		}

		return errs;
	}

	// -----------------------------------------------------------------------
	// Submit handler
	// -----------------------------------------------------------------------

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setSubmitted(true);

		const errs = validate();
		setErrors(errs);

		if (Object.keys(errs).length > 0) {
			return;
		}

		await onSubmit({
			companyName: companyName.trim(),
			name: name.trim(),
			email: email.trim(),
			jobTitle: jobTitle.trim(),
			projectRole: projectRole.trim(),
		});
	}

	// -----------------------------------------------------------------------
	// Derived validation state — only shown after first submit attempt
	// -----------------------------------------------------------------------

	const fieldError = (field: keyof ValidationErrors) =>
		submitted ? errors[field] : undefined;

	// -----------------------------------------------------------------------
	// Render
	// -----------------------------------------------------------------------

	return (
		<div className="client-user-form-overlay" role="presentation">
			<div
				className="client-user-form-modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby={headingId}
			>
				{/* ----- Heading ----- */}
				<h2 id={headingId} className="client-user-form__heading">
					Add Client User
				</h2>

				{/* ----- Success banner ----- */}
				{status === "success" && (
					<div className="client-user-form__success" role="status">
						Client user added successfully.
					</div>
				)}

				{/* ----- Error banner (only when status is explicitly "error") ----- */}
				{status === "error" && errorMessage && (
					<div className="client-user-form__error-banner" role="alert">
						{errorMessage}
					</div>
				)}

				{/* ----- Loading indicator ----- */}
				{isLoading && (
					<div className="client-user-form__loading-spinner" role="status" aria-label="Loading…">
						<span className="client-user-form__spinner" aria-hidden="true" />
					</div>
				)}

				<form
					className="client-user-form__form"
					onSubmit={handleSubmit}
					noValidate
				>
					{/* Company Name */}
					<div className="client-user-form__field">
						<label
							htmlFor="client-form-company"
							className="client-user-form__label"
						>
							Company Name <span aria-hidden="true">*</span>
						</label>
						<input
							id="client-form-company"
							type="text"
							className={
								fieldError("companyName")
									? "client-user-form__input client-user-form__input--error"
									: "client-user-form__input"
							}
							value={companyName}
							onChange={(e) => setCompanyName(e.target.value)}
							required
							aria-required="true"
							aria-invalid={fieldError("companyName") ? "true" : undefined}
							aria-describedby={
								fieldError("companyName") ? "client-form-company-error" : undefined
							}
							list={datalistId}
							disabled={isLoading}
							autoComplete="off"
						/>
						{/* Datalist for autocomplete */}
						<datalist id={datalistId}>
							{companies.map((c) => (
								<option key={c} value={c} />
							))}
						</datalist>
						{fieldError("companyName") && (
							<span
								id="client-form-company-error"
								className="client-user-form__field-error"
								role="alert"
							>
								{fieldError("companyName")}
							</span>
						)}
					</div>

					{/* Name */}
					<div className="client-user-form__field">
						<label
							htmlFor="client-form-name"
							className="client-user-form__label"
						>
							Name <span aria-hidden="true">*</span>
						</label>
						<input
							id="client-form-name"
							type="text"
							className={
								fieldError("name")
									? "client-user-form__input client-user-form__input--error"
									: "client-user-form__input"
							}
							value={name}
							onChange={(e) => setName(e.target.value)}
							required
							aria-required="true"
							aria-invalid={fieldError("name") ? "true" : undefined}
							aria-describedby={
								fieldError("name") ? "client-form-name-error" : undefined
							}
							disabled={isLoading}
						/>
						{fieldError("name") && (
							<span
								id="client-form-name-error"
								className="client-user-form__field-error"
								role="alert"
							>
								{fieldError("name")}
							</span>
						)}
					</div>

					{/* Email */}
					<div className="client-user-form__field">
						<label
							htmlFor="client-form-email"
							className="client-user-form__label"
						>
							Email <span aria-hidden="true">*</span>
						</label>
						<input
							id="client-form-email"
							type="email"
							className={
								fieldError("email")
									? "client-user-form__input client-user-form__input--error"
									: "client-user-form__input"
							}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							required
							aria-required="true"
							aria-invalid={fieldError("email") ? "true" : undefined}
							aria-describedby={
								fieldError("email") ? "client-form-email-error" : undefined
							}
							disabled={isLoading}
						/>
						{fieldError("email") && (
							<span
								id="client-form-email-error"
								className="client-user-form__field-error"
								role="alert"
							>
								{fieldError("email")}
							</span>
						)}
					</div>

					{/* Job Title (optional) */}
					<div className="client-user-form__field">
						<label
							htmlFor="client-form-job-title"
							className="client-user-form__label"
						>
							Job Title
						</label>
						<input
							id="client-form-job-title"
							type="text"
							className="client-user-form__input"
							value={jobTitle}
							onChange={(e) => setJobTitle(e.target.value)}
							disabled={isLoading}
						/>
					</div>

					{/* Project Role (optional) */}
					<div className="client-user-form__field">
						<label
							htmlFor="client-form-project-role"
							className="client-user-form__label"
						>
							Project Role
						</label>
						<input
							id="client-form-project-role"
							type="text"
							className="client-user-form__input"
							value={projectRole}
							onChange={(e) => setProjectRole(e.target.value)}
							disabled={isLoading}
						/>
					</div>

					{/* Actions */}
					<div className="client-user-form__actions">
						<button
							type="button"
							className="client-user-form__btn client-user-form__btn--cancel"
							onClick={onCancel}
							disabled={isLoading}
						>
							Cancel
						</button>
						<button
							type="submit"
							className="client-user-form__btn client-user-form__btn--submit"
							disabled={isLoading}
						>
							Add to Project
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
